import { Info, TrendingUp, AlertTriangle } from "lucide-react";

const fmt = (v) => (Number(v) % 1 === 0 ? `$${Number(v)}` : `$${Number(v).toFixed(2)}`);

const Section = ({ icon: Icon, title, children, tone = "border" }) => (
  <div className={`rounded-xl border p-4 space-y-2 ${tone === "primary" ? "border-primary/25 bg-primary/10" : tone === "warn" ? "border-amber-500/30 bg-amber-500/10" : "border-border bg-secondary/40"}`}>
    <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-primary" /><h4 className="font-heading font-bold text-sm">{title}</h4></div>
    {children}
  </div>
);

// Concise Founding Partner billing model for the onboarding page.
export const PartnerBillingExplainer = ({ setupFee = 99, className = "" }) => {
  const f = fmt(setupFee ?? 99);
  return (
    <div className={`space-y-3 ${className}`} data-testid="partner-billing-explainer">
      <Section icon={Info} title="Founding Partner" tone="primary">
        <dl className="text-xs text-muted-foreground space-y-2">
          <div>
            <dt className="font-semibold text-foreground">{f} one-time setup fee</dt>
            <dd>Paid once when you join. Includes your <strong className="text-foreground">first zone</strong> and <strong className="text-foreground">one year of participation</strong> — covers onboarding, your partner profile, Stripe setup and advertiser tools.</dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Additional zones (priced separately)</dt>
            <dd>Beyond your included first zone, extra zones are available for an additional annual fee that varies by territory based on traffic, rider volume and revenue potential.</dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Revenue split 50/50</dt>
            <dd>While a zone is leased, advertiser revenue is shared evenly with VeloPaths. One $99/year Founding Advertiser returns <strong className="text-foreground">$49.50</strong> to you.</dd>
          </div>
        </dl>
        <p className="text-xs text-muted-foreground pt-2 mt-2 border-t border-border/60" data-testid="example-partner-billing">
          <strong className="text-foreground">Renewal pricing may differ in future years</strong> as the platform expands and new features are introduced.
        </p>
      </Section>

      <Section icon={AlertTriangle} title="Good to know" tone="warn">
        <p className="text-xs text-amber-200/90">Your {f} Founding Partner one-time setup fee <strong>includes your first zone and one full year lease payment</strong>. Additional zones carry a separate annual lease fee, but no additional set-up fee.</p>
      </Section>
    </div>
  );
};

export const SetupFeeNote = ({ fee = 99, className = "" }) => {
  const f = fmt(fee ?? 99);
  return (
    <div className={`rounded-xl border border-primary/20 bg-primary/10 p-4 space-y-1 ${className}`} data-testid="setup-fee-note">
      <div className="flex items-center gap-2"><Info className="w-4 h-4 text-primary" /><h4 className="font-heading font-bold text-sm">One-time Founding Partner setup fee: {f}</h4></div>
      <p className="text-xs text-primary/90" data-testid="setup-vs-lease-note"><strong>First zone included</strong> · <strong>One year included</strong>. Additional zones are leased separately after approval, each with its own annual fee.</p>
    </div>
  );
};

export const RevenueShareNote = ({ className = "" }) => (
  <div className={`rounded-xl border border-border bg-secondary/40 p-4 space-y-1 ${className}`} data-testid="revenue-share-note">
    <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /><h4 className="font-heading font-bold text-sm">How you earn</h4></div>
    <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
      <li>Shops earn <strong className="text-foreground">50% of all advertiser revenue</strong></li>
      <li>Founding Advertisers pay <strong className="text-foreground">$99/year per location</strong></li>
      <li>Shops receive <strong className="text-foreground">$49.50/year</strong> per advertiser</li>
    </ul>
  </div>
);
